import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useParams, useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Phone,
  Pin,
  PanelRight,
  Settings,
  Hash,
  Users,
  Bell,
  BellOff,
  Star,
  StarOff,
  Archive,
  LogOut,
  Search,
  ChevronDown,
} from "lucide-react";
import { MessageList } from "@/components/MessageList";
import { MessageComposer } from "@/components/MessageComposer";
import { ThreadPane, threadRootFromMessage } from "@/features/threads";
import { isIntegrationMessage } from "@/lib/message-sender";
import { SafetyDialog, type SafetyTarget, useBlocks, unblockUser } from "@/components/SafetyDialog";
import type { User } from "@/lib/types";
import { SmartReplyChips, SummarizeButton, TaskExtractMenu } from "@/features/ai-assist";
import { useSession } from "@/lib/auth";
import { apiGet, apiPost, apiDelete, apiPatch } from "@/lib/api";
import { useRoomMessages } from "@/lib/messages";
import { useRooms, useRoomMembers, toClientRoom } from "@/lib/rooms";
import { useTaskAccess } from "@/features/tasks/access";
import { Button } from "@/components/ui/Button";
import { Avatar } from "@/components/ui/Avatar";
import { toggleSidebar, toggleContext, openContextTab } from "@/lib/uiState";
import { Menu } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/cn";

export function RoomPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  // 返信先や引用・候補文を、次のルームの入力欄へ持ち越さない。
  return <RoomContent key={id} />;
}

function RoomContent(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { rooms, loading: roomsLoading } = useRooms();
  const { data: members = [] } = useRoomMembers(id);
  const listedRoom = rooms.find((r) => r.id === id);
  const [apiRoom, setApiRoom] = useState<typeof listedRoom | null>(null);
  const [roomError, setRoomError] = useState<string | null>(null);
  const [roomLoading, setRoomLoading] = useState(false);
  useEffect(() => {
    setApiRoom(null); setRoomError(null);
    if (!id || listedRoom) return;
    let cancelled = false;
    setRoomLoading(true);
    apiGet<{ room: Parameters<typeof toClientRoom>[0] }>(`/v1/rooms/${encodeURIComponent(id)}`)
      .then(result => { if (!cancelled) setApiRoom(toClientRoom(result.room)); })
      .catch(error => { if (!cancelled) setRoomError(error.message); })
      .finally(() => { if (!cancelled) setRoomLoading(false); });
    return () => { cancelled = true; };
  }, [id, listedRoom]);
  const room = listedRoom ?? apiRoom;
  const taskAccess = useTaskAccess(room?.workspaceId || undefined);
  const canAssignTasks = taskAccess.isSuccess && taskAccess.data.can_assign;
  const canCreateTasks = taskAccess.isSuccess && taskAccess.data.can_create;
  // 作成先と同じワークスペースの権限・担当者候補を使う。
  const workspaceMembers = (taskAccess.data?.members ?? []).map(member => ({
    id: member.id,
    displayName: member.display_name || member.email,
  }));
  const readBoundary = useRef<{ roomId?: string; messageId?: string }>({});
  if (readBoundary.current.roomId !== id) readBoundary.current = { roomId: id };
  if (!readBoundary.current.messageId && room?.lastReadMessageId) readBoundary.current.messageId = room.lastReadMessageId;
  const chat = useRoomMessages(id);
  const { send, retry, typingUserIds } = chat;
  const blocks = useBlocks();
  const allMessages = chat.messages.map(m => ({
    ...m,
    authorName: isIntegrationMessage(m)
      ? (m.sender?.display_name || m.authorName)
      : (members.find(u => u.id === m.authorId)?.displayName || m.authorName),
  }));
  // Posts by members the viewer blocked are hidden, as in the mobile app (GET /v1/safety/blocks).
  const messages = blocks.blockedIds.size ? allMessages.filter(m => !blocks.blockedIds.has(m.authorId)) : allMessages;
  const hiddenAuthors = (blocks.data ?? []).filter(block => allMessages.some(m => m.authorId === block.user_id));
  const [searchParams, setSearchParams] = useSearchParams();
  const targetMessage = searchParams.get("message");
  const threadParam = searchParams.get("thread");
  useEffect(() => {
    if (!targetMessage) return;
    let cancelled = false;
    void chat.loadMessage(targetMessage).then((message) => {
      const root = message?.thread_root_id || message?.parent_id;
      if (cancelled || !root) return;
      setSearchParams((prev) => {
        if (prev.get("message") !== targetMessage || prev.get("thread")) return prev;
        const next = new URLSearchParams(prev);
        next.set("thread", root);
        return next;
      }, { replace: true });
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [targetMessage, chat.loadMessage, setSearchParams]);
  useEffect(() => {
    if (!targetMessage) return;
    const element = Array.from(document.querySelectorAll<HTMLElement>("[data-message-id]")).find(e => e.dataset.messageId === targetMessage);
    element?.scrollIntoView?.({ block: "center" });
  }, [targetMessage, chat.messages]);
  const lastRead = useRef("");
  const onRead = useCallback((messageId: string) => {
    if (!id || messageId === lastRead.current) return;
    lastRead.current = messageId;
    void apiPost(`/v1/rooms/${encodeURIComponent(id)}/read`, { last_read_message_id: messageId }).catch(() => { lastRead.current = ""; });
  }, [id]);
  const [actionError, setActionError] = useState<string | null>(null);
  // 投稿の「タスク化」は成功しても画面に何も出ず、作れたかがタスク一覧を開くまで分からなかった
  // （2026-10-02、ChatGPT の dot による利用者目線の確認で指摘）。成功したら一言とタスク一覧への導線を出す。
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const perform = (work: Promise<unknown>) => { setActionError(null); void work.catch(e => setActionError(e instanceof Error ? e.message : String(e))); };
  const { t } = useTranslation();
  const session = useSession();
  const [suggestion, setSuggestion] = useState("");
  const [showReplies, setShowReplies] = useState(false);
  const aiContext = useMemo(() => chat.messages.slice(-20).map(m => ({ message_id: m.id, author: m.authorName || m.authorId, body: m.body, created_at: new Date(m.createdAt).getTime(), is_self: m.authorId === session?.user.id })), [chat.messages, session?.user.id]);

  const [replyingTo, setReplyingTo] = useState<{
    id: string;
    author: string;
    preview: string;
  } | null>(null);
  const openThread = useCallback((rootId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("thread", rootId);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const closeThread = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("thread");
    next.delete("message");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const onReply = useCallback(
    (mid: string) => {
      const m = messages.find((mm) => mm.id === mid);
      if (!m) return;
      setReplyingTo({
        id: mid,
        author: m.authorName || m.authorId,
        preview: m.body.slice(0, 80),
      });
      openThread(threadRootFromMessage(m));
    },
    [messages, openThread],
  );

  const [quoteRequest, setQuoteRequest] = useState<{ text: string; nonce: number } | null>(null);
  const onQuote = useCallback(
    (mid: string) => {
      const m = messages.find((mm) => mm.id === mid);
      if (!m) return;
      const author = m.authorName || m.authorId;
      const quoted = [`**${author}**`, ...m.body.split(/\r?\n/)].map(line => `> ${line}`).join("\n");
      setQuoteRequest({ text: `${quoted}\n\n`, nonce: Date.now() });
    },
    [messages],
  );
  const [safetyTarget, setSafetyTarget] = useState<SafetyTarget | null>(null);
  useEffect(() => { setQuoteRequest(null); setSafetyTarget(null); }, [id]);
  const openSafety = useCallback(
    (mode: SafetyTarget["mode"], mid: string) => {
      const m = messages.find((mm) => mm.id === mid);
      if (!m || !id) return;
      setSafetyTarget({
        mode,
        roomId: id,
        userId: m.authorId,
        displayName: m.authorName || "ユーザー",
        message: mode === "report" ? { id: m.id, body: m.body } : undefined,
      });
    },
    [messages, id],
  );

  const onCopy = useCallback(
    async (mid: string) => {
      const m = messages.find((mm) => mm.id === mid);
      if (!m) return;
      try {
        await navigator.clipboard?.writeText(m.body);
      } catch {
        /* clipboard may be unavailable */
      }
    },
    [messages],
  );

  if (!id) return <Navigate to="/" replace />;
  if (!room && (roomsLoading || roomLoading)) return <div role="status" className="p-6">ルームを読み込んでいます…</div>;
  if (!room) {
    return (
      <>
        <RoomHeader title="—" subtitle={undefined} memberCount={0} />
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center px-6 py-12 bg-surface-base">
          <p className="text-md font-medium text-fg-primary">
            {roomError || t("room.notFound", { defaultValue: "ルームが見つかりません。" }) as string}
          </p>
          <p className="text-sm text-fg-muted">
            {t("room.notFoundHint", {
              defaultValue: "削除されたか、アクセス権がない可能性があります。",
            }) as string}
          </p>
        </div>
      </>
    );
  }

  const subtitle =
    room.kind === "channel"
      ? (t("nav.rooms") as string)
      : room.kind === "group"
        ? (t("common.group", { defaultValue: "グループ" }) as string)
        : (t("nav.dm") as string);

  return (
    <>
      <RoomHeader
        title={room.name}
        subtitle={subtitle}
        memberCount={members.length}
        pinned={room.pinned}
      />
      <div className="@container flex-1 min-h-0 flex">
      <div className={cn("flex-1 min-w-0 flex-col min-h-0", threadParam ? "hidden @min-[48rem]:flex" : "flex")}>
      {actionNotice && !actionError && <p role="status" className="p-3 text-success">{actionNotice}<a href="/tasks" className="ml-3 underline">タスク一覧を開く</a></p>}
      {(chat.error || actionError) && <p role="alert" className="p-3 text-danger">{actionError || chat.error?.message}<button onClick={() => void chat.refresh()} className="ml-3 underline">再読み込み</button></p>}
      <p role="status" className="px-4 text-xs text-fg-muted">{chat.loading ? "メッセージを読み込んでいます…" : chat.connection === "open" ? "接続中" : "再接続しています…"}</p>
      {hiddenAuthors.length > 0 && (
        <div className="px-4 py-1 text-xs text-fg-muted flex flex-wrap items-center gap-2">
          <span>ブロック中のメンバーの投稿は表示していません。</span>
          {hiddenAuthors.map(block => (
            <button
              key={block.user_id}
              type="button"
              className="underline text-brand-700"
              onClick={() => {
                const name = block.display_name || "ユーザー";
                if (window.confirm(`${name} のブロックを解除しますか？`)) perform(unblockUser(block.user_id, session?.tenantId, session?.user.id));
              }}
            >
              {block.display_name || "ユーザー"} のブロックを解除
            </button>
          ))}
        </div>
      )}
      <MessageList
        key={`messages:${id}`}
        lastReadId={readBoundary.current.messageId}
        messages={messages}
        onRead={onRead}
        onRetry={retry}
        onReply={onReply}
        onOpenThread={openThread}
        onCopy={onCopy}
        onQuote={onQuote}
        onReport={(mid) => openSafety("report", mid)}
        onBlock={(mid) => openSafety("block", mid)}
        currentUserId={session?.user.id}
        onLoadEarlier={chat.loadEarlier}
        loadingEarlier={chat.loadingEarlier}
        reachedStart={chat.reachedStart}
        onDelete={(mid) => { if (window.confirm("このメッセージを削除しますか？")) perform(chat.remove(mid)); }}
        onEdit={(mid) => { const original = messages.find(m => m.id === mid); const body = window.prompt("メッセージを編集", original?.body); if (body?.trim()) perform(chat.edit(mid, body)); }}
        onReact={(mid) => perform(chat.react(mid))}
        onPin={(mid) => perform(chat.pin(mid))}
        onTaskify={(mid) => { const message = messages.find(m => m.id === mid); if (message) { setActionNotice(null); perform(apiPost("/v1/tasks/promote-from-message", { message_id: mid, room_id: id, title: message.body.slice(0, 120) }).then(() => setActionNotice("タスクを作成しました。"))); } }}
      />
      {typingUserIds.length > 0 ? (
        <TypingIndicator userIds={typingUserIds} members={members} />
      ) : null}
      <div className="flex items-center gap-3 px-4 py-2">
        <SummarizeButton roomId={id} fetchMessages={async (range) => {
          const since = range === "day" ? Date.now() - 86400000 : range === "week" ? Date.now() - 604800000 : 0;
          const records: import("@/lib/messages").ServerMessage[] = [];
          let cursor: string | null | undefined;
          do {
            const page: { messages: import("@/lib/messages").ServerMessage[]; next_cursor?: string | null; has_more?: boolean } = await apiGet(`/v1/rooms/${encodeURIComponent(id)}/messages?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`);
            records.push(...page.messages.filter(message => message.created_at >= since && !message.deleted_at));
            if (!page.has_more || !page.messages.length || page.messages.some(message => message.created_at < since) || page.next_cursor === cursor) break;
            cursor = page.next_cursor;
          } while (cursor && records.length < 500);
          return records.sort((a, b) => b.created_at - a.created_at).slice(0, 500).reverse().map(message => ({
            author: members.find(member => member.id === message.user_id)?.displayName
              || message.sender?.display_name
              || message.user_id
              || "アプリ",
            body: message.body_md,
            created_at: message.created_at,
          }));
        }} />
        {messages.length > 0 && <TaskExtractMenu messageId={messages[messages.length - 1].id} messages={aiContext} members={workspaceMembers} canAssign={canAssignTasks} canCreate={canCreateTasks} currentUserId={session?.user.id} onCreate={async task => { await apiPost("/v1/tasks", { title: task.title, description: task.description, room_id: id, assignee: task.assignee ?? undefined, due_at: task.due_date_hint ? Date.parse(task.due_date_hint) || undefined : undefined }); }} />}
        <Button size="sm" variant="ghost" onClick={() => setShowReplies(v => !v)} disabled={!messages.length}>AI 返信候補</Button>
      </div>
      {showReplies && messages.length > 0 && <SmartReplyChips messageId={messages[messages.length - 1].id} context={aiContext} onSelect={setSuggestion} />}
      <MessageComposer
        key={`composer:${id}`}
        draftKey={`ailechat:draft:${session?.user.id}:${session?.tenantId}:${id}`}
        initialValue={suggestion}
        members={members}
        onSend={(body, extras) => {
          send(body, { ...extras, parentId: replyingTo?.id });
          setReplyingTo(null);
        }}
        onTyping={chat.sendTyping}
        insertRequest={quoteRequest}
        replyingTo={replyingTo}
        onCancelReply={() => setReplyingTo(null)}
      />
      </div>
      {threadParam ? (
        <ThreadPane
          roomId={id}
          threadRootId={threadParam}
          members={members}
          onClose={closeThread}
          highlightMessageId={targetMessage}
        />
      ) : null}
      </div>
      {safetyTarget && <SafetyDialog key={`${safetyTarget.mode}:${safetyTarget.message?.id ?? safetyTarget.userId}`} target={safetyTarget} onClose={() => setSafetyTarget(null)} />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Header                                                              */
/* ------------------------------------------------------------------ */

interface RoomHeaderProps {
  title: string;
  subtitle?: string;
  memberCount: number;
  pinned?: boolean;
}

function RoomHeader({
  title,
  subtitle,
  memberCount,
  pinned,
}: RoomHeaderProps): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <header className="@container/room-header h-14 shrink-0 px-4 flex items-center gap-2 border-b border-border-subtle bg-canvas">
      <button
        type="button"
        aria-label={t("shortcuts.toggleSidebar") as string}
        onClick={() => toggleSidebar()}
        className="p-1.5 rounded hover:bg-surface-raised text-fg-muted lg:hidden"
      >
        <Menu size={18} aria-hidden />
      </button>
      <span
        aria-hidden
        className="w-7 h-7 rounded-md bg-brand-50 text-brand-700 flex items-center justify-center shrink-0"
      >
        <Hash size={14} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <h1 className="text-md font-bold flex items-center gap-1.5 truncate">
          <span className="truncate">{title}</span>
          {pinned ? (
            <Pin size={11} aria-hidden className="text-fg-muted" />
          ) : null}
        </h1>
        <div className="text-xs text-fg-muted flex items-center gap-2 truncate">
          {subtitle ? <span className="truncate">{subtitle}</span> : null}
          {memberCount > 0 ? (
            <>
              <span aria-hidden>·</span>
              <button
                type="button"
                onClick={() => openContextTab("members")}
                className="inline-flex items-center gap-1 hover:underline focus-visible:shadow-focus rounded"
              >
                <Users size={11} aria-hidden />
                <span>
                  {t("room.memberCount", {
                    count: memberCount,
                    defaultValue: "{{count}} 名",
                  }) as string}
                </span>
              </button>
            </>
          ) : null}
        </div>
      </div>
      <div className="hidden md:flex shrink-0 items-center gap-1">
        <button
          type="button"
          aria-label={t("nav.search") as string}
          title={t("nav.search") as string}
          onClick={() => navigate(`/search?room=${encodeURIComponent(id || "")}`)}
          className="p-1.5 rounded hover:bg-surface-raised text-fg-muted"
        >
          <Search size={16} aria-hidden />
        </button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label={t("call.start")}
          title={t("call.start")}
          className="shrink-0 whitespace-nowrap px-1.5 @min-[28rem]/room-header:px-3"
          onClick={() => navigate(`/calls/${encodeURIComponent(id || "")}`)}
        >
          <span className="inline-flex items-center gap-2">
            <Phone size={14} aria-hidden />
            <span className="hidden @min-[28rem]/room-header:inline">{t("call.start")}</span>
          </span>
        </Button>
      </div>
      <RoomSettingsDropdown roomTitle={title} />
      <button
        type="button"
        aria-label={t("shortcuts.toggleContext") as string}
        onClick={() => toggleContext()}
        className="p-1.5 rounded hover:bg-surface-raised text-fg-muted"
      >
        <PanelRight size={18} aria-hidden />
      </button>
    </header>
  );
}

function RoomSettingsDropdown({ roomTitle: _roomTitle }: { roomTitle: string }): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { id } = useParams();
  const session = useSession();
  const navigate = useNavigate();
  const preferences = useQuery({ queryKey: ["room-preferences", id, session?.user.id], enabled: !!id,
    queryFn: () => apiGet<{ notification_level: "all" | "mentions" | "none"; starred: boolean }>(`/v1/rooms/${encodeURIComponent(id!)}/preferences`) });
  const muted = preferences.data?.notification_level === "none";
  const starred = preferences.data?.starred ?? false;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const update = async (work: () => Promise<unknown>, leave = false) => {
    if (!id || busy) return;
    setBusy(true); setError(null);
    try { await work(); await preferences.refetch(); await queryClient.invalidateQueries({ queryKey: ["rooms"] }); setOpen(false); if (leave) navigate("/rooms"); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const changePreference = (value: Record<string, unknown>) => update(() => apiPatch(`/v1/rooms/${encodeURIComponent(id!)}/preferences`, value));
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("nav.settings") as string}
        title={t("nav.settings") as string}
        className="p-1.5 rounded hover:bg-surface-raised text-fg-muted inline-flex items-center"
      >
        <Settings size={16} aria-hidden />
        <ChevronDown size={12} aria-hidden className="ml-0.5" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 min-w-56 bg-canvas border border-border-subtle rounded-md shadow-lg py-1 text-sm z-30"
        >
          {(error || preferences.error) && <p role="alert" className="text-danger px-3 py-2">{error || preferences.error?.message}</p>}
          <DropdownItem
            disabled={busy || !preferences.data}
            icon={muted ? <Bell size={13} aria-hidden /> : <BellOff size={13} aria-hidden />}
            label={
              muted
                ? (t("room.unmute", { defaultValue: "通知を有効化" }) as string)
                : (t("room.mute", { defaultValue: "通知をミュート" }) as string)
            }
            onClick={() => {
              // null returns the room to its default: DMs notify on every message, other rooms on mentions.
              void changePreference({ notification_level: muted ? null : "none" });
            }}
          />
          <DropdownItem
            disabled={busy || !preferences.data}
            icon={starred ? <StarOff size={13} aria-hidden /> : <Star size={13} aria-hidden />}
            label={
              starred
                ? (t("room.unstar", { defaultValue: "スターを外す" }) as string)
                : (t("room.star", { defaultValue: "スター" }) as string)
            }
            onClick={() => {
              void changePreference({ starred: !starred });
            }}
          />
          <DropdownItem
            icon={<Pin size={13} aria-hidden />}
            label={t("home.pinnedMessages", { defaultValue: "ピン留めされた投稿" }) as string}
            onClick={() => {
              openContextTab("pinned");
              setOpen(false);
            }}
          />
          <DropdownItem
            icon={<Users size={13} aria-hidden />}
            label={t("common.members") as string}
            onClick={() => {
              openContextTab("members");
              setOpen(false);
            }}
          />
          <div className="my-1 border-t border-border-subtle" aria-hidden />
          <DropdownItem
            icon={<Archive size={13} aria-hidden />}
            label={t("room.archive", { defaultValue: "アーカイブ" }) as string}
            disabled={busy}
            onClick={() => { if (window.confirm("このルームをアーカイブしますか？")) void update(() => apiPost(`/v1/rooms/${encodeURIComponent(id!)}/archive`, {}), true); }}
          />
          <DropdownItem
            icon={<LogOut size={13} aria-hidden />}
            label={t("room.leave", { defaultValue: "ルームを退出" }) as string}
            tone="danger"
            disabled={busy}
            onClick={() => { if (window.confirm("このルームから退出しますか？")) void update(() => apiDelete(`/v1/rooms/${encodeURIComponent(id!)}/members/${encodeURIComponent(session!.user.id)}`), true); }}
          />
        </div>
      ) : null}
    </div>
  );
}

function DropdownItem({
  icon,
  label,
  onClick,
  tone = "default",
  disabled,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  tone?: "default" | "danger";
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-surface-raised focus-visible:bg-surface-raised focus:outline-none",
        tone === "danger" ? "text-danger" : "text-fg-primary",
      )}
    >
      <span className="text-fg-muted">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Typing indicator                                                    */
/* ------------------------------------------------------------------ */

function TypingIndicator({ userIds, members }: { userIds: string[]; members: User[] }): JSX.Element {
  const { t } = useTranslation();
  const names = useMemo(
    () =>
      userIds
        .map((id) => members.find(u => u.id === id)?.displayName)
        .filter((s): s is string => typeof s === "string"),
    [userIds],
  );
  if (names.length === 0) return <></>;
  return (
    <div
      aria-live="polite"
      className="px-4 py-1 text-xs text-fg-muted bg-canvas border-t border-border-subtle flex items-center gap-2"
    >
      <span className="flex gap-0.5" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="w-1 h-1 rounded-full bg-fg-muted animate-pulse"
            style={{ animationDelay: `${i * 120}ms` }}
          />
        ))}
      </span>
      <Avatar name={names[0]} size={20} />
      <span>
        {t("compose.typing", {
          name: names.join("、"),
          count: names.length,
          defaultValue:
            names.length === 1
              ? "{{name}} さんが入力中…"
              : "{{name}} さんが入力中…",
        }) as string}
      </span>
    </div>
  );
}
